<script setup lang="ts">
/**
 * RinganVarianKompres — badan halaman varian target ukuran (mis. /kompres-foto/200kb),
 * versi tanpa Nuxt UI. Padanan <CaVarianKompres>.
 *
 * Struktur, kelas, dan urutan bagiannya identik dengan CaVarianKompres.vue;
 * yang ditukar hanya komponen anaknya. <CaCard> tetap dipakai langsung karena
 * memang murni CSS.
 *
 * Seluruh isi tetap datang dari utils/varianKompres.ts — satu sumber untuk
 * versi lama, layout utama, maupun versi ringan, supaya salinan SEO-nya tidak
 * pernah bercabang.
 */
import type { VarianKompres } from '~/utils/varianKompres'

defineProps<{
  jenis: 'foto' | 'pdf'
  varian: VarianKompres
  /** Path induk (mis. /kompres-foto) — untuk remah & tautan balik. */
  indukPath: string
  indukNama: string
}>()
</script>

<template>
  <div>
    <!-- HERO -->
    <section class="relative overflow-hidden">
      <div class="ca-aura" aria-hidden="true" />

      <div class="relative mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 pt-8 sm:pt-12 pb-10">
        <RinganRemah :jejak="[['Beranda', '/'], [indukNama, indukPath], [`${varian.targetKb} KB`, '']]" />

        <div class="mt-5 grid gap-9 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:items-start">
          <div class="min-w-0">
            <RinganLencana tone="emas-lembut" icon="gauge">
              Target {{ varian.targetKb }} KB
            </RinganLencana>

            <h1 class="ca-h1 mt-4 text-ink text-balance">{{ varian.h1 }}</h1>

            <p class="mt-4 text-lg text-muted leading-relaxed max-w-xl">{{ varian.intro }}</p>
          </div>

          <!-- Alatnya sendiri, langsung di lipatan atas: pengunjung halaman ini
               datang untuk memakai, bukan untuk membaca. -->
          <RinganKompresAlat :jenis="jenis" :target-awal="varian.targetKb" />
        </div>
      </div>
    </section>

    <!-- URAIAN -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <div class="max-w-3xl flex flex-col gap-4">
          <p
            v-for="(p, i) in varian.paragraf"
            :key="i"
            class="text-[1.0625rem] text-ink leading-relaxed"
          >{{ p }}</p>
        </div>

        <!-- TABEL BATAS UKURAN -->
        <div v-if="varian.tabel" class="mt-10">
          <RinganJudulBagian :level="3" judul="Batas ukuran per dokumen" />

          <CaCard flush class="mt-5 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-2 border-b border-line">
                    <th
                      v-for="k in varian.tabel.kolom"
                      :key="k"
                      scope="col"
                      class="px-4 py-3 font-bold text-ink whitespace-nowrap"
                    >{{ k }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(b, i) in varian.tabel.baris"
                    :key="i"
                    class="border-b border-line last:border-0"
                  >
                    <td
                      v-for="(sel, j) in b"
                      :key="j"
                      class="px-4 py-3 leading-relaxed align-top"
                      :class="j === 0 ? 'text-ink' : 'text-muted whitespace-nowrap'"
                    >{{ sel }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CaCard>

          <RinganPesan tone="warn" title="Angka ini bisa berubah" class="mt-4 max-w-3xl">
            {{ varian.tabel.catatan }}
          </RinganPesan>
        </div>
      </div>
    </section>

    <!-- AJAKAN -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganAjakanKilat />
      </div>
    </section>

    <!-- FAQ -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganFaq :daftar="varian.faq" badge="Sebelum mengunggah" />
      </div>
    </section>

    <!-- TAUTAN LAIN -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian :level="3" judul="Target ukuran lainnya" />
        <div class="mt-5 max-w-3xl">
          <slot name="tautan-lain" />
        </div>
      </div>
    </section>
  </div>
</template>
